import React, { useState, useEffect, useRef } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  Slide,
  useTheme,
  useMediaQuery,
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { AIAgentService } from '../services/aiAgentService';
import { isFeatureEnabled } from '../config/features';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const FloatingChatWidget = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Redux user state
  const { user } = useSelector((state) => state.user);
  const isAuthenticated = user !== null && user !== undefined;
  
  // Chat state
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Generate conversation ID when first message is sent
  useEffect(() => {
    if (!conversationId && messages.length > 0) {
      setConversationId(AIAgentService.generateConversationId());
    }
  }, [messages, conversationId]);

  // Clear error when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated) {
      setError(null);
    }
  }, [isAuthenticated]);

  // Return null if AI features are disabled (after all hooks are called)
  if (!isFeatureEnabled('AI_CHAT_WIDGET')) {
    return null;
  }

  const handleOpen = () => {
    setIsOpen(true);
    // Add welcome message if no messages
    if (messages.length === 0) {
      if (isAuthenticated) {
        addBotMessage("Hello! I'm your meeting room assistant. I can help you book rooms, check availability, and manage your bookings. How can I assist you today?");
      } else {
        addBotMessage("Hello! I'm your meeting room assistant. Please log in to start using the chat feature.");
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const addUserMessage = (message) => {
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
  };

  const addBotMessage = (message, suggestions = [], actionResult = null) => {
    const botMessage = {
      id: Date.now() + 1,
      type: 'bot',
      content: message,
      timestamp: new Date(),
      suggestions: suggestions || [],
      actionResult: actionResult
    };
    setMessages(prev => [...prev, botMessage]);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    if (!isAuthenticated) {
      toast.error('Please log in to use the chat feature');
      return;
    }

    const message = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    // Add user message immediately
    addUserMessage(message);

    try {
      const result = await AIAgentService.sendMessage(message, conversationId);

      if (result.success) {
        const response = result.data;
        addBotMessage(
          response.message,
          response.suggestions || [],
          response.actionResult
        );
      } else {
        addBotMessage(
          result.error || 'Sorry, I encountered an error. Please try again.',
          ['Try again', 'Contact support']
        );
        setError(result.error);
      }
    } catch (error) {
      console.error('Chat error:', error);
      addBotMessage(
        'Sorry, I\'m having trouble connecting. Please try again in a moment.',
        ['Retry', 'Refresh chat']
      );
      setError('Connection error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputMessage(suggestion);
    inputRef.current?.focus();
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    addBotMessage("Chat cleared! How can I help you today?");
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Message component
  const ChatMessage = ({ message }) => {
    const isBot = message.type === 'bot';
    
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: isBot ? 'row' : 'row-reverse',
          mb: 2,
          alignItems: 'flex-start'
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: isBot ? theme.palette.primary.main : theme.palette.secondary.main,
            mx: 1
          }}
        >
          {isBot ? <BotIcon /> : <PersonIcon />}
        </Avatar>
        
        <Box sx={{ maxWidth: '75%' }}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              bgcolor: isBot ? 'grey.100' : 'primary.main',
              color: isBot ? 'text.primary' : 'primary.contrastText',
              borderRadius: 2,
              borderTopLeftRadius: isBot ? 0 : 2,
              borderTopRightRadius: isBot ? 2 : 0
            }}
          >
            <Typography variant="body2">
              {message.content}
            </Typography>
            
            {/* Action Result Display */}
            {message.actionResult && (
              <Box sx={{ mt: 1 }}>
                {message.actionResult.success && message.actionResult.data && (
                  <Paper sx={{ p: 1, mt: 1, bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary">
                      Results:
                    </Typography>
                    {Array.isArray(message.actionResult.data) ? (
                      <List dense>
                        {message.actionResult.data.slice(0, 3).map((item, index) => (
                          <ListItem key={index} disablePadding>
                            <ListItemText 
                              primary={item.title || item.name || `Item ${index + 1}`}
                              secondary={item.subtitle || item.description}
                            />
                          </ListItem>
                        ))}
                        {message.actionResult.data.length > 3 && (
                          <Typography variant="caption" color="text.secondary">
                            +{message.actionResult.data.length - 3} more...
                          </Typography>
                        )}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.primary">
                        {message.actionResult.data.toString()}
                      </Typography>
                    )}
                  </Paper>
                )}
              </Box>
            )}
          </Paper>
          
          {/* Timestamp */}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {formatTimestamp(message.timestamp)}
          </Typography>
          
          {/* Suggestions */}
          {message.suggestions && message.suggestions.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {message.suggestions.map((suggestion, index) => (
                <Chip
                  key={index}
                  label={suggestion}
                  size="small"
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="open chat"
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          bottom: isMobile ? 16 : 24,
          right: isMobile ? 16 : 24,
          zIndex: 1000
        }}
      >
        <ChatIcon />
      </Fab>

      {/* Chat Dialog */}
      <Dialog
        open={isOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            height: isMobile ? '100%' : '70vh',
            maxHeight: isMobile ? '100%' : '70vh',
            borderRadius: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BotIcon color="primary" />
            <Typography variant="h6">Meeting Room Assistant</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={handleClearChat} size="small">
              <ClearIcon />
            </IconButton>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            overflow: 'hidden'
          }}
        >
          {/* Error Display */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              action={
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => setError(null)}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}
          
          {/* Messages Container */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              mb: 2,
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a1a1a1',
              },
            }}
          >
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            
            {/* Loading indicator */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            
            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </Box>
        </DialogContent>
        
        <Divider />
        
        {/* Input Area */}
        <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <TextField
              ref={inputRef}
              fullWidth
              variant="outlined"
              placeholder={isAuthenticated ? "Type your message..." : "Please log in to chat"}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading || !isAuthenticated}
              size="small"
              multiline
              maxRows={3}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading || !isAuthenticated}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              <SendIcon />
            </Button>
          </Box>
          
          {!isAuthenticated && (
            <Alert severity="info" sx={{ width: '100%' }}>
              Please log in to use the chat feature
            </Alert>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FloatingChatWidget; 